import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertTriangle, CalendarClock, CheckCircle2, Search } from "lucide-react";
import { fmtDate } from "@/components/people/overview/programUtils";

const SOURCE_LABEL = { stage_entered: "Pipeline stage history (system date)", corrected: "Pipeline stage history (corrected)",
  opportunity_opened: "Opportunity opened", enrollment_target: "Program enrollment target" };

function ConflictRow({ person, c, onResolved }) {
  const options = [
    ...(c.stage_value ? [{ v: c.stage_value, label: SOURCE_LABEL[c.stage_source] || "Pipeline stage history" }] : []),
    ...c.others.map((o) => ({ v: o.value, label: o.label })),
  ];
  const [choice, setChoice] = useState("");
  const [custom, setCustom] = useState("");
  const [saving, setSaving] = useState(false);
  const date = choice === "__custom__" ? custom : choice;
  const key = `${person.person_id}-${c.milestone}`;
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/date-review/resolve", { person_id: person.person_id, milestone: c.milestone, date });
      toast.success(`${c.label} set to ${fmtDate(date)} for ${person.name}`);
      onResolved(person.person_id, data.remaining);
    } catch (e) { toast.error(apiError(e)); setSaving(false); }
  };
  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid={`dr-conflict-${key}`}>
      <p className="text-sm font-medium text-slate-800 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-amber-600" />{c.label}</p>
      <div className="mt-2 space-y-1.5" role="radiogroup" aria-label={`${c.label} date`}>
        {options.map((o, i) => (
          <label key={i} className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="radio" name={key} checked={choice === o.v} onChange={() => setChoice(o.v)} data-testid={`dr-opt-${key}-${i}`} />
            <span className="tabular-nums font-medium text-slate-900 w-28">{fmtDate(o.v)}</span><span className="text-slate-500 text-xs">{o.label}</span>
          </label>
        ))}
        <label className="flex flex-wrap items-center gap-2 text-sm cursor-pointer">
          <input type="radio" name={key} checked={choice === "__custom__"} onChange={() => setChoice("__custom__")} data-testid={`dr-opt-${key}-custom`} />
          <span className="text-slate-600">A different date</span>
          {choice === "__custom__" && <input type="date" value={custom} onChange={(e) => setCustom(e.target.value)} className="border border-slate-300 rounded px-2 py-1 text-sm" data-testid={`dr-custom-${key}`} />}
        </label>
      </div>
      <div className="flex justify-end mt-2">
        <Button size="sm" onClick={save} disabled={!date || saving} className="bg-brand hover:bg-brand-strong" data-testid={`dr-resolve-${key}`}>Confirm this date</Button>
      </div>
    </div>
  );
}

export default function DateReview() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/date-review").then((r) => setData(r.data)).catch((e) => { toast.error(apiError(e)); setData({ items: [] }); }); }, []);
  const onResolved = (pid, remaining) => setData((d) => ({ ...d, items: d.items.map((p) => (p.person_id === pid ? { ...p, conflicts: remaining } : p)).filter((p) => p.conflicts.length) }));
  const items = useMemo(() => (data?.items || []).filter((p) => `${p.name} ${p.human_id} ${p.organization_name}`.toLowerCase().includes(q.toLowerCase())), [data, q]);
  return (
    <div className="max-w-5xl" data-testid="date-review-page">
      <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2"><CalendarClock className="w-6 h-6 text-brand" />Milestone Date Review</h1>
      <p className="text-sm text-slate-500 mt-1 mb-5 max-w-3xl">Conflicting milestone dates are resolved automatically: the oldest valid date for the same milestone, within the same recruiting lifecycle, wins and is written to the stage history and every saved copy. Import, migration and later snapshot dates are never used, and later dates from any sync, import or edit can't replace an earlier recorded date. Only a confirmed date exception (Account Admin, with a reason) keeps a later date. Each change is recorded in the audit log.</p>
      {data === null ? <p className="text-sm text-slate-400">Loading…</p> : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <p className="text-sm text-slate-700" data-testid="dr-count"><b>{data.items.length}</b> {data.items.length === 1 ? "person needs" : "people need"} review</p>
            <div className="relative w-full sm:w-72"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, ID or group" className="pl-8" data-testid="dr-search" /></div>
          </div>
          {!data.items.length ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-800 flex items-center gap-2" data-testid="dr-empty"><CheckCircle2 className="w-4 h-4" />No milestone dates need review.</div>
          ) : (
            <div className="space-y-4">
              {items.map((p) => (
                <section key={p.person_id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`dr-person-${p.person_id}`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                    <Link to={`/people/detail/${p.person_id}`} className="font-semibold text-slate-900 hover:text-brand hover:underline" data-testid={`dr-person-link-${p.person_id}`}>{p.name}</Link>
                    <span className="text-xs text-slate-400">{p.human_id} · {p.organization_name || "No group"}{p.stage ? ` · ${p.stage}` : ""}</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {p.conflicts.map((c) => <ConflictRow key={c.milestone} person={p} c={c} onResolved={onResolved} />)}
                  </div>
                </section>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
